import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const RealtimeContentContext = createContext(null);

// Reconnect constants
const MAX_RETRIES = 5;
const BASE_DELAY = 500;

export const useRealtimeContent = () => {
  const context = useContext(RealtimeContentContext);
  if (!context) {
    throw new Error('useRealtimeContent must be used within RealtimeProvider');
  }
  return context;
};

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('CLOSED');
  const channel = useRef(null);
  const reconnectTimeout = useRef(null);
  const retryCount = useRef(0);
  const statusCallbackRef = useRef(null);

  // Fetch latest state from database
  const fetchLatestState = () => {
    if (!user?.id) return;
    
    supabase
      .schema('public')
      .from('instagram_content')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (data) {
          // Merge with existing items to preserve any updates
          setItems(prev => {
            const merged = new Map();
            
            // First, add existing items
            prev.forEach(item => {
              if (item?.id) merged.set(item.id, item);
            });
            
            // Then, merge in fresh data (fresh data takes precedence for conflicting items)
            data.forEach(item => {
              if (item?.id) {
                const existing = merged.get(item.id);
                // Merge, but preserve any newer progress updates from realtime
                if (existing && existing.progress_percentage !== undefined && item.progress_percentage !== undefined) {
                  // Keep the higher progress if one is newer
                  const existingUpdated = new Date(existing.updated_at || existing.created_at);
                  const itemUpdated = new Date(item.updated_at || item.created_at);
                  merged.set(item.id, {
                    ...item,
                    progress_percentage: itemUpdated > existingUpdated 
                      ? item.progress_percentage 
                      : Math.max(existing.progress_percentage || 0, item.progress_percentage || 0)
                  });
                } else {
                  merged.set(item.id, item);
                }
              }
            });
            
            return Array.from(merged.values());
          });
          console.log(`✅ Synced ${data.length} items from database`);
        }
      });
  };

  // Dedicated reconnect function with exponential backoff and jitter
  const attemptReconnect = () => {
    if (retryCount.current >= MAX_RETRIES) {
      console.warn('[Realtime] Max retries reached, stopping attempts');
      return;
    }

    retryCount.current++;
    const delay = BASE_DELAY * Math.pow(2, retryCount.current - 1);
    const jitter = Math.random() * 200;
    
    console.log(`[Realtime] Attempting reconnect #${retryCount.current} in ${Math.round(delay + jitter)}ms`);

    // Clear any existing timeout
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }

    reconnectTimeout.current = setTimeout(() => {
      // Check if channel still exists and user hasn't changed
      if (!channel.current || !user?.id) {
        console.warn('[Realtime] Skipping reconnect - channel or user no longer exists');
        return;
      }

      try {
        // Resubscribe with the stored status callback
        // This ensures the status callback is called on reconnect
        if (statusCallbackRef.current) {
          channel.current.subscribe(statusCallbackRef.current);
        } else {
          // Fallback: resubscribe without callback (uses existing callback if channel is still set up)
          channel.current.subscribe();
        }
      } catch (err) {
        console.error('[Realtime] Reconnect attempt failed', err);
        attemptReconnect();
      }
    }, delay + jitter);
  };

  //  Initialize subscription
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔄 Initializing realtime subscription for user:', user.id);

    // Reset retry count on new subscription
    retryCount.current = 0;

    // Subscribe to BOTH instagram_content AND deepsearch.items
    channel.current = supabase
      .channel(`content_updates_${user.id}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'instagram_content',
          filter: `user_id=eq.${user.id}` // Filter by user_id
        },
        (payload) => {
          console.log('📨 Realtime event:', payload.eventType, payload.new?.id);
          
          // Update immediately for progress bars (no debounce)
          setItems(prev => {
            const newItems = [...prev];
            const itemId = payload.new?.id;
            
            if (!itemId) return prev;
            
            if (payload.eventType === 'INSERT') {
              // New item - add to beginning
              const exists = newItems.findIndex(i => i.id === itemId);
              if (exists === -1) {
                return [payload.new, ...newItems];
              }
            } else if (payload.eventType === 'UPDATE') {
              // Update existing item - merge carefully
              const index = newItems.findIndex(i => i.id === itemId);
              if (index !== -1) {
                // Merge, preserving any fields that might not be in the update
                newItems[index] = { ...newItems[index], ...payload.new };
              } else {
                // Item not in list yet, add it
                newItems.unshift(payload.new);
              }
            } else if (payload.eventType === 'DELETE') {
              return newItems.filter(i => i.id !== itemId);
            }
            
            return newItems;
          });
        }
      )
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'deepsearch', 
          table: 'items',
          filter: `user_id=eq.${user.id}` // Filter by user_id
        },
        (payload) => {
          console.log('📨 Realtime event (deepsearch.items):', payload.eventType, payload.new?.id);
          
          // Update immediately for progress bars (no debounce)
          setItems(prev => {
            const newItems = [...prev];
            const itemId = payload.new?.id;
            
            if (!itemId) return prev;
            
            if (payload.eventType === 'INSERT') {
              // New item - add to beginning
              const exists = newItems.findIndex(i => i.id === itemId);
              if (exists === -1) {
                return [payload.new, ...newItems];
              }
            } else if (payload.eventType === 'UPDATE') {
              // Update existing item - merge carefully
              const index = newItems.findIndex(i => i.id === itemId);
              if (index !== -1) {
                // Merge, preserving any fields that might not be in the update
                newItems[index] = { ...newItems[index], ...payload.new };
              } else {
                // Item not in list yet, add it
                newItems.unshift(payload.new);
              }
            } else if (payload.eventType === 'DELETE') {
              return newItems.filter(i => i.id !== itemId);
            }
            
            return newItems;
          });
        }
      );
    
    // Store status callback for reuse during reconnects
    statusCallbackRef.current = (newStatus) => {
      console.log('📡 Realtime status:', newStatus);
      setStatus(newStatus);

      // On successful subscribe, fetch latest state
      if (newStatus === 'SUBSCRIBED') {
        console.log('🔄 Reconnected — fetching latest state');
        retryCount.current = 0;
        fetchLatestState();
      }

      // Handle connection issues with throttled reconnect
      if (newStatus === 'CHANNEL_ERROR') {
        console.log('[Realtime] Channel error detected, scheduling reconnect');
        attemptReconnect();
      }
    };
    
    channel.current.subscribe(statusCallbackRef.current);

    return () => {
      console.log('🧹 [RealtimeContentContext] Cleaning up subscription...');
      
      // Clear reconnect timeout to prevent reconnection attempts
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      
      // Unsubscribe channel to prevent memory leaks
      if (channel.current) {
        try {
          channel.current.unsubscribe();
        } catch (error) {
          console.warn('⚠️ Error unsubscribing channel:', error);
        }
        channel.current = null;
      }
      
      // Reset retry count on unmount
      retryCount.current = 0;
      statusCallbackRef.current = null;
      
      console.log('✅ [RealtimeContentContext] Cleanup complete');
    };
  }, [user?.id]);

  const reconnect = () => {
    console.log('🔄 Manual reconnect requested');
    if (channel.current) {
      channel.current.unsubscribe();
      channel.current.subscribe();
    }
  };

  const upsertItem = (item) => {
    console.log('📝 Upserting item:', item.id);
    setItems(prev => {
      const index = prev.findIndex(i => i.id === item.id);
      if (index !== -1) {
        const updated = [...prev];
        updated[index] = { ...updated[index], ...item };
        return updated;
      } else {
        return [item, ...prev];
      }
    });
  };

  return (
    <RealtimeContentContext.Provider
      value={{
        items,
        status,
        upsertItem,
        reconnect
      }}
    >
      {children}
    </RealtimeContentContext.Provider>
  );
};

export default RealtimeProvider;

